import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering,
    FormattedRpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { AssertionAttribute } from "@idw-types/data/reference-data/generic";
import { AddRpServiceConfigurationServiceOfferingAssertionModal } from "@containers/modals/add-rp-service-configuration-service-offering-assertion-modal/add-rp-service-configuration-service-offering-assertion-modal";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ValidatedApAssertions } from "./ap-assertions-missing-attributes-alert/ap-assertions-missing-attributes-alert";
import commonStyles from "../../verification-service-details-component.less";
import styles from "./ap-assertions-tab-container.less";

type ApAssertionsTabContainerProps = {
    isDisabled: boolean;
    rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    assertionAttributes: Array<AssertionAttribute>
    setRpServiceConfigurationServiceOffering: (serviceOffering: FormattedRpServiceConfigurationServiceOffering) => void;
};

export const ApAssertionsTabContainer = (props: ApAssertionsTabContainerProps) => {
    const {
        isDisabled,
        rpServiceConfigurationServiceOffering,
        rpServiceConfigurationState,
        assertionAttributes,
        setRpServiceConfigurationServiceOffering
    } = props;
    const modalContext = useModalContext();

    const selectedAssertions = useMemo(() => {
        return rpServiceConfigurationServiceOffering.assertions.filter(assertion => assertion.addedToService && assertion.status === EntityStatusTypes.ACTIVE);
    }, [rpServiceConfigurationServiceOffering.assertions]);

    const unselectedAssertions = useMemo(() => {
        const selectedAssertionApAssertionIds = selectedAssertions.map(sa => sa.apAssertionId);
        const apAssertionIds = rpServiceConfigurationServiceOffering.assertions.map(assertion => assertion.apAssertionId);
        
        const filteredAssertions = rpServiceConfigurationServiceOffering.assertions.filter((assertion, index) =>
            assertion.apAssertionStatus === EntityStatusTypes.ACTIVE &&
            (!assertion.addedToService || (assertion.addedToService && assertion.status !== EntityStatusTypes.ACTIVE)) &&
            !selectedAssertionApAssertionIds.includes(assertion.apAssertionId) &&
            apAssertionIds.indexOf(assertion.apAssertionId) === index
        );

        return filteredAssertions;

    }, [rpServiceConfigurationServiceOffering.assertions, selectedAssertions]);

    const selectedAttributes = useMemo(() => {
        return rpServiceConfigurationState.attributes.filter(attribute => attribute.status === EntityStatusTypes.ACTIVE && attribute.addedToService);

    }, [rpServiceConfigurationState.attributes]);

    const addRpServiceConfigurationServiceOfferingAssertion = (assertionToAdd: FormattedRpServiceConfigurationServiceOfferingAssertion) => {
        const newAssertions = rpServiceConfigurationServiceOffering.assertions.map(assertion => assertion.uuid === assertionToAdd.uuid
            ? {
                ...assertionToAdd,
                addedToService: true,
                status: EntityStatusTypes.ACTIVE
            }
            : assertion
        );
        setRpServiceConfigurationServiceOffering({
            ...rpServiceConfigurationServiceOffering,
            assertions: newAssertions
        });
    };

    const onClickAddAssertion = () => {
        const renderAddAttributeModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationServiceOfferingAssertionModal
                    rpServiceConfigurationServiceOfferingAssertions={unselectedAssertions}
                    addRpServiceConfigurationServiceOfferingAssertion={addRpServiceConfigurationServiceOfferingAssertion}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddAttributeModal,
            modalType: ModalTypes.AddAttribute
        });
    };

    const assertionsHeaderButton = (
        //todo - what permissions are needed to add assertions?
        !isDisabled ? (
            <Button id="add-assertion" disabled={unselectedAssertions.length === 0} onClick={onClickAddAssertion}>
                {resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.buttons.addAssertion.label}
            </Button>
        ) : null
    );


    return (
        <div className={styles.apAssertionsTabContainer}>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.title(rpServiceConfigurationServiceOffering.name)}
                    headerButton={assertionsHeaderButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>
                        {resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.description(rpServiceConfigurationServiceOffering.name)}
                    </span>
                    {unselectedAssertions.length === 0 && (
                        <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.allApAssertionsMessage(rpServiceConfigurationServiceOffering.name)}</Alert>
                    )}
                    {selectedAssertions.length === 0 && (
                        <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.noApAssertionsMessage(rpServiceConfigurationServiceOffering.name, isDisabled)}</Alert>
                    )}
                    {selectedAssertions.length > 0 && (
                        <div className={styles.apAssertionsContainer}>
                            {selectedAssertions.map(assertion => {
                                return (
                                    //todo - what permissions are needed to remove assertions?
                                    <ValidatedApAssertions
                                        key={`ap-assertion-missing-attribute-${assertion.apAssertionId}`}
                                        rpServiceConfigurationState={rpServiceConfigurationState}
                                        assertionAttributes={assertionAttributes}
                                        selectedAttributes={selectedAttributes}
                                        selectedAssertion={assertion}
                                        rpServiceConfigurationServiceOffering={rpServiceConfigurationServiceOffering}
                                        setRpServiceConfigurationServiceOffering={setRpServiceConfigurationServiceOffering}
                                        isDisabled={isDisabled}
                                    />
                                )
                            })}
                        </div>
                    )}
                </ContentPageBody>
            </div>
        </div>
    );
};